import {
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
  Flex,
  Divider,
  Text,
  Image,
  Box,
  Heading,
  DrawerFooter,
  Progress,
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
} from '@chakra-ui/react';
import { MdWarning } from 'react-icons/md';

import Button from 'components/Button';
import FallbackAvatar from 'components/FallbackAvatar';
import RequestStatus from 'containers/TimeOff/shared/RequestStatus/RequestStatus';

type TRequestDetailsProps = {
  handleSingleApprove?: (id: number, categoryType: string) => void;
  handleSingleReject?: (id: number) => void;
  onClose?: () => void;
  requestDetails?: any;
};

const RequestDetails: React.FC<TRequestDetailsProps> = ({
  requestDetails,
  handleSingleApprove,
  handleSingleReject,
}) => {
  return (
    <DrawerContent>
      <DrawerCloseButton top="4" />
      <DrawerHeader display="flex" justifyContent="space-between" pr="60px">
        <Flex alignItems="center">
          <Image
            src={requestDetails?.employee_image}
            fallback={<FallbackAvatar name={requestDetails?.full_name} />}
            alt={requestDetails?.full_name}
            boxSize="60px"
            objectFit="cover"
            borderRadius="lg"
          />
          <Box ml="3" fontSize="md">
            <Text fontWeight={700} color="newGray.1000">
              {requestDetails?.full_name}
            </Text>
            <Text color="gray.500" fontSize="sm">
              {requestDetails?.user_role}
            </Text>
          </Box>
        </Flex>
      </DrawerHeader>
      <DrawerBody mt="3">
        {requestDetails?.category_is_deleted ? (
          <Alert status="warning" variant="top-accent" mb="3" p="2">
            <AlertIcon as={MdWarning} />
            <div>
              <AlertTitle>This category is no longer active.</AlertTitle>
              <AlertDescription> Displayed data reflects its state prior to deletion.</AlertDescription>
            </div>
          </Alert>
        ) : null}
        <Flex gridGap={{ base: '4', md: '8' }} flexDirection={{ base: 'column', md: 'row' }}>
          <Box>
            <Text textTransform="uppercase" fontSize="xs" color="gray.500" fontWeight="semibold" mb="1">
              Category
            </Text>
            <Text fontWeight="semibold">{requestDetails?.category_name || ''}</Text>
          </Box>
          <Box>
            <Text textTransform="uppercase" fontSize="xs" color="gray.500" fontWeight="semibold" mb="1">
              Request Status
            </Text>

            <RequestStatus status={requestDetails?.status?.toLowerCase()} />
          </Box>
          <Box>
            <Text textTransform="uppercase" fontSize="xs" color="gray.500" fontWeight="semibold" mb="1">
              Requested On
            </Text>
            <Text fontWeight="semibold">{requestDetails?.requested_at || ''}</Text>
          </Box>
        </Flex>

        <Divider my="5" borderBottomWidth="2px" borderStyle="dashed" />

        <Heading as="h5" size="sm" mb="4">
          Details
        </Heading>

        <Flex>
          <Text
            textTransform="uppercase"
            fontSize="12px"
            color="gray.500"
            fontWeight="semibold"
            flex={{ base: '0 0 100px', sm: '0 0 200px' }}
            py="2"
          >
            Period
          </Text>
          <Text fontWeight="semibold" py="2">
            {requestDetails?.period}
          </Text>
        </Flex>
        <Flex>
          <Text
            textTransform="uppercase"
            fontSize="12px"
            color="gray.500"
            fontWeight="semibold"
            flex={{ base: '0 0 100px', sm: '0 0 200px' }}
            py="2"
          >
            Type
          </Text>
          <Text fontWeight="semibold" py="2">
            {requestDetails?.category_type}
          </Text>
        </Flex>
        <Flex>
          <Text
            textTransform="uppercase"
            fontSize="12px"
            color="gray.500"
            fontWeight="semibold"
            flex={{ base: '0 0 100px', sm: '0 0 200px' }}
            py="2"
          >
            Duration
          </Text>
          <Text fontWeight="semibold" py="2">
            {requestDetails?.duration || requestDetails?.request_duration}
          </Text>
        </Flex>
        <Flex>
          <Text
            textTransform="uppercase"
            fontSize="12px"
            color="gray.500"
            fontWeight="semibold"
            flex={{ base: '0 0 100px', sm: '0 0 200px' }}
            py="2"
          >
            Comment
          </Text>
          <Text fontWeight="semibold" py="2">
            {requestDetails?.description}
          </Text>
        </Flex>

        <Flex>
          <Text
            textTransform="uppercase"
            fontSize="12px"
            color="gray.500"
            fontWeight="semibold"
            flex={{ base: '0 0 100px', sm: '0 0 200px' }}
            py="2"
          >
            Remaining
          </Text>
          <Flex alignItems="center" flex="1 1 auto" gridGap="8px" fontWeight="semibold">
            <Text>{requestDetails?.user_category_balance?.balance}</Text>{' '}
            <Progress
              flex="1 1 auto"
              value={requestDetails?.user_category_balance?.percentage * 100}
              height="5"
              colorScheme="primary"
              borderRadius="md"
            />{' '}
            <Text>{requestDetails?.user_category_balance?.used}</Text>
          </Flex>
        </Flex>

        {requestDetails?.reviewer ? (
          <>
            <Flex mt="4">
              <Text
                textTransform="uppercase"
                fontSize="12px"
                color="gray.500"
                fontWeight="semibold"
                flex={{ base: '0 0 100px', sm: '0 0 200px' }}
                py="2"
              >
                Reviewer
              </Text>
              <Box>
                <Flex alignItems="center" _notLast={{ mb: '2' }}>
                  <Image
                    fallback={<FallbackAvatar name={requestDetails?.reviewer?.name} boxSize="32px" />}
                    boxSize="32px"
                    objectFit="cover"
                    borderRadius="base"
                    src={requestDetails?.reviewer?.image}
                    alt={requestDetails?.reviewer?.name}
                  />
                  <Text ml="2">{requestDetails?.reviewer?.name}</Text>
                </Flex>
              </Box>
            </Flex>
            <Flex mt="4">
              <Text
                textTransform="uppercase"
                fontSize="12px"
                color="gray.500"
                fontWeight="semibold"
                flex={{ base: '0 0 100px', sm: '0 0 200px' }}
                py="2"
              >
                Declined/Withdrawn <br />
                Reason
              </Text>
              <Text fontWeight="semibold" py="2">
                {requestDetails?.reviewer?.comment}
              </Text>
            </Flex>
          </>
        ) : null}
      </DrawerBody>
      {!requestDetails?.is_approve && !requestDetails.is_reject && !requestDetails?.category_is_deleted ? (
        <DrawerFooter borderTop="1px solid" borderColor="gray.200">
          {requestDetails?.can_reject && handleSingleReject ? (
            <Button
              background="newRed.1000"
              _hover={{ background: 'secondary.400' }}
              _active={{ background: 'secondary.400' }}
              mr="4"
              onClick={() => {
                handleSingleReject(requestDetails?.id || '');
              }}
            >
              Reject
            </Button>
          ) : null}
          {requestDetails?.can_approve && handleSingleApprove ? (
            <Button
              background="success.500"
              _hover={{ background: 'green.400' }}
              _active={{ background: 'green.400' }}
              onClick={() => {
                handleSingleApprove(requestDetails?.id || '', requestDetails?.category_type || '');
              }}
            >
              Approve
            </Button>
          ) : null}
        </DrawerFooter>
      ) : null}
    </DrawerContent>
  );
};

export default RequestDetails;
